import {
  Badge,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';

type LegacyKeyValueSectionProps = {
  title: string;
  data: Record<string, unknown> | null | undefined;
  emptyLabel?: string;
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

export function LegacyKeyValueSection({
  title,
  data,
  emptyLabel = 'No data',
}: LegacyKeyValueSectionProps) {
  const entries = data ? Object.entries(data) : [];

  return (
    <Paper withBorder p='md' radius='md'>
      <Group justify='space-between' mb='sm'>
        <Title order={4}>{title}</Title>
        <Badge variant='light' color='gray'>
          {entries.length} fields
        </Badge>
      </Group>
      {entries.length === 0 ? (
        <Text c='dimmed' size='sm'>
          {emptyLabel}
        </Text>
      ) : (
        <ScrollArea>
          <Table striped highlightOnHover withTableBorder>
            <Table.Tbody>
              {entries.map(([key, value]) => (
                <Table.Tr key={key}>
                  <Table.Td w={220}>
                    <Text fw={600} size='sm'>
                      {key}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm' style={{ wordBreak: 'break-word' }}>
                      {formatValue(value)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}
    </Paper>
  );
}

type LegacyRowsSectionProps = {
  title: string;
  rows: Record<string, unknown>[] | null | undefined;
  emptyLabel?: string;
};

export function LegacyRowsSection({
  title,
  rows,
  emptyLabel = 'No rows',
}: LegacyRowsSectionProps) {
  const safeRows = rows ?? [];
  const columns =
    safeRows.length > 0
      ? Array.from(
          safeRows.reduce((set, row) => {
            Object.keys(row).forEach((key) => set.add(key));
            return set;
          }, new Set<string>())
        )
      : [];

  return (
    <Paper withBorder p='md' radius='md'>
      <Group justify='space-between' mb='sm'>
        <Title order={4}>{title}</Title>
        <Badge variant='light' color='gray'>
          {safeRows.length} rows
        </Badge>
      </Group>
      {safeRows.length === 0 ? (
        <Text c='dimmed' size='sm'>
          {emptyLabel}
        </Text>
      ) : (
        <ScrollArea>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                {columns.map((col) => (
                  <Table.Th key={col}>{col}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {safeRows.map((row, index) => (
                <Table.Tr key={index}>
                  {columns.map((col) => (
                    <Table.Td key={col}>
                      <Text size='xs' style={{ wordBreak: 'break-word' }}>
                        {formatValue(row[col])}
                      </Text>
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}
    </Paper>
  );
}

export function LegacyStatsSection({
  title,
  stats,
}: {
  title: string;
  stats: Record<string, number>;
}) {
  return (
    <Paper withBorder p='md' radius='md'>
      <Stack gap='xs'>
        <Title order={4}>{title}</Title>
        <Group gap='md'>
          {Object.entries(stats).map(([key, value]) => (
            <Badge key={key} size='lg' variant='light'>
              {key}: {value}
            </Badge>
          ))}
        </Group>
      </Stack>
    </Paper>
  );
}

const STAGING_EMPTY =
  '0 rows on staging vapor (OLD_DB). Production v1 admin may still have data for this user.';

/** Mirrors become-fe-2023 AdminTokenPage columns for user_devices. */
export function LegacyDevicesSection({
  devices,
  connections,
  userEmail,
}: {
  devices: Record<string, unknown>[] | null | undefined;
  connections: Record<string, unknown>[] | null | undefined;
  userEmail?: string | null;
}) {
  const rows = devices ?? [];
  const conns = connections ?? [];

  return (
    <Stack gap='md'>
      <Paper withBorder p='md' radius='md'>
        <Group justify='space-between' mb='sm'>
          <Title order={4}>user_devices</Title>
          <Badge variant='light' color='gray'>
            {rows.length} rows
          </Badge>
        </Group>
        {rows.length === 0 ? (
          <Text c='dimmed' size='sm'>
            {STAGING_EMPTY}
          </Text>
        ) : (
          <ScrollArea>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>id</Table.Th>
                  <Table.Th>email</Table.Th>
                  <Table.Th>device_name</Table.Th>
                  <Table.Th>device_type</Table.Th>
                  <Table.Th>one_time_password</Table.Th>
                  <Table.Th>disponibile</Table.Th>
                  <Table.Th>last_login_at</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map(row => {
                  const available = row.last_login_at == null;
                  return (
                    <Table.Tr key={String(row.id)}>
                      <Table.Td>{String(row.id ?? '—')}</Table.Td>
                      <Table.Td>
                        <Text size='sm' style={{ wordBreak: 'break-all' }}>
                          {userEmail || '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>{String(row.device_name ?? '—')}</Table.Td>
                      <Table.Td>{String(row.device_type ?? '—')}</Table.Td>
                      <Table.Td>
                        <Text size='sm' ff='monospace'>
                          {String(row.one_time_password ?? '—')}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size='sm'
                          variant='light'
                          color={available ? 'green' : 'red'}
                        >
                          {available ? 'sì' : 'no'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size='xs'>
                          {String(row.last_login_at ?? '—')}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>

      <LegacyRowsSection
        title='user_device_connections'
        rows={conns}
        emptyLabel={STAGING_EMPTY}
      />
    </Stack>
  );
}

/** Mirrors become-fe-2023 UserHasTestQuestionsModal (retrieveCourseTests → user_answers). */
export function LegacyTestAnswersSection({
  rows,
  courses,
}: {
  rows: Record<string, unknown>[] | null | undefined;
  courses?: Record<string, unknown>[] | null;
}) {
  const safeRows = rows ?? [];
  const safeCourses = courses ?? [];
  const groups = new Map<
    string,
    { attempt: number; created_at: string; items: Record<string, unknown>[] }
  >();

  for (const row of safeRows) {
    const attempt = Number(row.attempt ?? 0);
    const created_at = String(row.created_at ?? '');
    const key = `${attempt}|${created_at}`;
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(row);
    } else {
      groups.set(key, { attempt, created_at, items: [row] });
    }
  }

  const coursesWithAttempts = safeCourses.filter(
    c => Number(c.number_of_attempts) > 0
  );

  return (
    <Paper withBorder p='md' radius='md'>
      <Group justify='space-between' mb='sm'>
        <Title order={4}>
          Test answers (user_answers · same as old admin retrieveCourseTests)
        </Title>
        <Badge variant='light' color='gray'>
          {safeRows.length} rows · {groups.size} attempts
        </Badge>
      </Group>
      {safeRows.length === 0 ? (
        <Stack gap='xs'>
          <Text c='dimmed' size='sm'>
            0 rows in staging `OLD_DB`. Quiz payloads live in the vapor dump
            SQLite — run services `yarn import-v1` (sets `data/v1/source.sqlite`
            / `V1_SQLITE_PATH`) so detail/export/migrate hydrate by email.
          </Text>
          {coursesWithAttempts.length > 0 ? (
            <Text size='sm'>
              Entitlements show attempts on{' '}
              {coursesWithAttempts.length} course(s) (
              {coursesWithAttempts
                .map(
                  c =>
                    `#${c.course_id ?? c.courseId}: ${c.number_of_attempts} att.`
                )
                .join(', ')}
              ) but answer payloads are not on staging.
            </Text>
          ) : null}
        </Stack>
      ) : (
        <Stack gap='md'>
          {[...groups.values()].map((group) => (
            <Stack key={`${group.attempt}-${group.created_at}`} gap='xs'>
              <Text size='sm' fw={700}>
                Attempt n. {group.attempt + 1}
                {group.created_at
                  ? ` of ${new Date(group.created_at).toLocaleString()}`
                  : ''}
              </Text>
              {group.items.map((item, index) => {
                const correct = Boolean(item.is_correct);
                return (
                  <Paper
                    key={String(item.id ?? index)}
                    withBorder
                    p='sm'
                    style={{
                      background: correct
                        ? 'rgba(64, 192, 87, 0.12)'
                        : 'rgba(250, 82, 82, 0.12)',
                    }}
                  >
                    <Text size='sm' fw={600}>
                      {String(
                        item.question_text ??
                          item.description ??
                          `question #${item.question}`
                      )}
                    </Text>
                    <Group justify='space-between' mt={4}>
                      <Text size='sm'>{String(item.answer ?? '—')}</Text>
                      <Badge color={correct ? 'green' : 'red'} variant='light'>
                        {correct ? 'correct' : 'wrong'}
                      </Badge>
                    </Group>
                    {item.course_name ? (
                      <Text size='xs' c='dimmed' mt={4}>
                        {String(item.course_name)}
                      </Text>
                    ) : null}
                  </Paper>
                );
              })}
            </Stack>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

const formatAmountCents = (value: unknown): string => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return (n / 100).toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
  });
};

/** Readable billing view: Cashier subscriptions + Stripe payment_intents (+ cart). */
export function LegacyBillingSection({
  subscriptions,
  subscriptionItems,
  paymentIntents,
}: {
  subscriptions: Record<string, unknown>[] | null | undefined;
  subscriptionItems: Record<string, unknown>[] | null | undefined;
  paymentIntents: Record<string, unknown>[] | null | undefined;
}) {
  const subs = subscriptions ?? [];
  const items = subscriptionItems ?? [];
  const pis = paymentIntents ?? [];

  return (
    <Stack gap='md'>
      <Paper withBorder p='md' radius='md'>
        <Group justify='space-between' mb='sm'>
          <Title order={4}>subscriptions (Cashier)</Title>
          <Badge variant='light' color='gray'>
            {subs.length} rows
          </Badge>
        </Group>
        {subs.length === 0 ? (
          <Text c='dimmed' size='sm'>
            0 subscriptions on this vapor DB (Cashier empty on staging; v1 dump
            had schema but no subscription rows).
          </Text>
        ) : (
          <ScrollArea>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>id</Table.Th>
                  <Table.Th>name</Table.Th>
                  <Table.Th>stripe_id</Table.Th>
                  <Table.Th>status</Table.Th>
                  <Table.Th>price</Table.Th>
                  <Table.Th>ends_at</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {subs.map((row) => (
                  <Table.Tr key={String(row.id)}>
                    <Table.Td>{String(row.id ?? '—')}</Table.Td>
                    <Table.Td>{String(row.name ?? '—')}</Table.Td>
                    <Table.Td>
                      <Text size='xs' style={{ wordBreak: 'break-all' }}>
                        {String(row.stripe_id ?? '—')}
                      </Text>
                    </Table.Td>
                    <Table.Td>{String(row.stripe_status ?? '—')}</Table.Td>
                    <Table.Td>{String(row.stripe_price ?? '—')}</Table.Td>
                    <Table.Td>{String(row.ends_at ?? '—')}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>

      <LegacyRowsSection
        title='subscription_items'
        rows={items}
        emptyLabel='0 items (no subscriptions linked)'
      />

      <Paper withBorder p='md' radius='md'>
        <Group justify='space-between' mb='sm'>
          <Title order={4}>payment_intents</Title>
          <Badge variant='light' color='orange'>
            {pis.length} rows
          </Badge>
        </Group>
        {pis.length === 0 ? (
          <Text c='dimmed' size='sm'>
            No payment_intents matched by email (PI has no user_id; join is
            case-insensitive on email). Staging has few rows with email set.
          </Text>
        ) : (
          <ScrollArea>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>id</Table.Th>
                  <Table.Th>status</Table.Th>
                  <Table.Th>amount</Table.Th>
                  <Table.Th>stripe PI</Table.Th>
                  <Table.Th>cart</Table.Th>
                  <Table.Th>created</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pis.map((row) => (
                  <Table.Tr key={String(row.id)}>
                    <Table.Td>{String(row.id ?? '—')}</Table.Td>
                    <Table.Td>
                      <Badge
                        size='sm'
                        variant='light'
                        color={
                          row.status === 'succeeded'
                            ? 'green'
                            : row.status === 'created'
                              ? 'gray'
                              : 'orange'
                        }
                      >
                        {String(row.status ?? '—')}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{formatAmountCents(row.amount)}</Table.Td>
                    <Table.Td>
                      <Text size='xs' style={{ wordBreak: 'break-all' }}>
                        {String(row.stripe_payment_intent_id ?? '—')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='sm'>
                        {String(
                          row.cart_summary ||
                            (Array.isArray(row.cart_product_names)
                              ? (row.cart_product_names as string[]).join('; ')
                              : '—')
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='xs'>{String(row.created_at ?? '—')}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>
    </Stack>
  );
}
